import { RelationObjectType } from "@enums/relations.enum";
import { IAttribute, IAttributeFieldValue } from "@interfaces/attribute.interface";
import ToastService from "@services/toast.service";
import { FormLabel } from "@ui/FormElements";

import { AttributeField } from "./AttributeField";
import { CommonAttributeIcon } from "./CommonAttributeIcon";
import { getEffectiveAttributeValue, isAttributeRequired, validateAttributeValue } from "./utils";

interface AttributeRowProps {
  attribute: IAttribute;
  // Entity being edited; resolves the org's per-entity-type required flag.
  contentType: RelationObjectType;
  // The field's react-hook-form path, mirrored onto the DOM so scrollToFirstError can find it.
  rowId: string;
  value: IAttributeFieldValue;
  // The entry as the server has it. What a cleared required field is put back to.
  savedValue?: IAttributeFieldValue;
  onChange: (value: IAttributeFieldValue) => void;
  isInvalid: boolean;
  errorMessage?: string;
  currencySymbol: string;
  isCommon: boolean;
  showCommonIcon: boolean;
  // Never offered by RELATION search — typically the entity being edited.
  excludedRelationIds?: string[];
  onCommonAttributeClick?: (attribute: IAttribute) => void;
  onAttributeSave?: (value: IAttributeFieldValue, isCommon: boolean) => void;
}

export function AttributeRow({
  attribute,
  contentType,
  rowId,
  value,
  savedValue,
  onChange,
  isInvalid,
  errorMessage,
  currencySymbol,
  isCommon,
  showCommonIcon,
  excludedRelationIds,
  onCommonAttributeClick,
  onAttributeSave,
}: AttributeRowProps) {
  const isRequired = isAttributeRequired(attribute, contentType, isCommon);

  // Inline save (existing company only). Submit-time validation covers the creation forms, so
  // without a save handler there is nothing to reject here.
  const saveIfValid = (next: IAttributeFieldValue) => {
    if (!onAttributeSave) return;

    const nextValue = getEffectiveAttributeValue(next);
    const error = validateAttributeValue(attribute, nextValue, contentType, isCommon);

    if (typeof error === "string") {
      ToastService.showUserError(<span>{error}</span>, { id: `attribute-${attribute.id}` });
      // A cleared required field goes back to what the server holds; a badly formatted one keeps
      // the user's text to fix. Never a local edit — an unsaved one would be restored as if stored.
      if (!nextValue && isRequired && savedValue) onChange(savedValue);
      return;
    }

    onAttributeSave(next, isCommon);
  };

  return (
    <div id={rowId} className="flex flex-col gap-0.5">
      <FormLabel htmlFor={attribute.id} isInvalid={isInvalid}>
        <span className="flex w-full items-center justify-between gap-1">
          <span className="min-w-0 break-words">
            {attribute.title}
            {isRequired && <span className="ml-0.5">*</span>}
          </span>
          {showCommonIcon && (
            <CommonAttributeIcon
              onClick={onCommonAttributeClick ? () => onCommonAttributeClick(attribute) : undefined}
            />
          )}
        </span>
      </FormLabel>
      <AttributeField
        attribute={attribute}
        value={value.value}
        currencySymbol={currencySymbol}
        isInvalid={isInvalid}
        errorMessage={errorMessage}
        relations={value.relations}
        excludedRelationIds={excludedRelationIds}
        onChange={(next) => onChange({ ...value, value: next })}
        onSave={(next) => saveIfValid({ ...value, value: next })}
        // Keeps form state live while open...
        onRelationsChange={(next) => onChange({ ...value, relations: next })}
        // ...and saves only once it settles, so a batch of picks is a single request.
        onRelationsSave={(next) => saveIfValid({ ...value, relations: next })}
      />
    </div>
  );
}
